
import { useState } from 'react'
import { useSiteConfig, useUpdateSiteConfig } from '@/hooks/useCMS'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { Save } from 'lucide-react'

export const CMSGeneralSettings = () => {
  const { data: config, isLoading } = useSiteConfig()
  const updateConfig = useUpdateSiteConfig()
  const [formData, setFormData] = useState<{ [key: string]: string }>({})

  const generalConfig = config?.filter(item => item.section === 'general') || []
  const heroConfig = config?.filter(item => item.section === 'hero') || []
  const contactConfig = config?.filter(item => item.section === 'contact') || []

  const handleSave = async () => {
    try {
      const updates = Object.entries(formData).map(([key, value]) => {
        const configItem = config?.find(item => `${item.section}.${item.key}` === key)
        if (configItem) {
          return updateConfig.mutateAsync({
            section: configItem.section,
            key: configItem.key,
            value,
            type: configItem.type
          })
        }
        return Promise.resolve()
      })

      await Promise.all(updates)
      toast.success('Paramètres sauvegardés avec succès!')
      setFormData({})
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde')
      console.error(error)
    }
  }

  const handleInputChange = (section: string, key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [`${section}.${key}`]: value
    }))
  }

  const getValue = (section: string, key: string) => {
    const formKey = `${section}.${key}`
    if (formData[formKey] !== undefined) {
      return formData[formKey]
    }
    return config?.find(item => item.section === section && item.key === key)?.value || ''
  }

  if (isLoading) {
    return <div>Chargement...</div>
  }

  return (
    <div className="space-y-8">
      {/* Informations générales */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Informations du site</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="site_title">Titre du site</Label>
            <Input
              id="site_title"
              value={getValue('general', 'site_title')}
              onChange={(e) => handleInputChange('general', 'site_title', e.target.value)}
              placeholder="Titre du site"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="site_description">Description du site</Label>
            <Textarea
              id="site_description"
              value={getValue('general', 'site_description')}
              onChange={(e) => handleInputChange('general', 'site_description', e.target.value)}
              placeholder="Description du site"
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Section Hero */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Section Hero (Page d'accueil)</h3>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="main_title">Titre principal</Label>
            <Input
              id="main_title"
              value={getValue('hero', 'main_title')}
              onChange={(e) => handleInputChange('hero', 'main_title', e.target.value)}
              placeholder="Titre principal de la page d'accueil"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subtitle">Sous-titre</Label>
            <Textarea
              id="subtitle"
              value={getValue('hero', 'subtitle')}
              onChange={(e) => handleInputChange('hero', 'subtitle', e.target.value)}
              placeholder="Sous-titre de la page d'accueil"
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Informations de contact */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Informations de contact</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Téléphone</Label>
            <Input
              id="phone"
              value={getValue('contact', 'phone')}
              onChange={(e) => handleInputChange('contact', 'phone', e.target.value)}
              placeholder="Numéro de téléphone"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={getValue('contact', 'email')}
              onChange={(e) => handleInputChange('contact', 'email', e.target.value)}
              placeholder="Adresse email"
            />
          </div>
        </div>
      </div>

      {/* Bouton de sauvegarde */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={updateConfig.isPending || Object.keys(formData).length === 0}
          className="bg-gradient-to-r from-pink-500 to-blue-500"
        >
          <Save className="w-4 h-4 mr-2" />
          {updateConfig.isPending ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>
    </div>
  )
}
